import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  X,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Megaphone,
} from "lucide-react";


/**
 * Component that displays broadcast notifications as popups
 * Should be placed at root level to be visible on all pages
 */
export function BroadcastNotifications() {
  const [broadcasts, setBroadcasts] = useState([]);
  const { token, getAuthHeaders } = useAuth();

  // Fetch active broadcasts on mount and periodically
  useEffect(() => {
    if (!token) return;

    const fetchBroadcasts = async () => {
      try {
        const response = await axios.get(`${API_URL}/live-support/broadcasts`, {
          headers: getAuthHeaders(),
        });
        setBroadcasts(response.data.broadcasts || []);
      } catch (error) {
        console.error("Failed to fetch broadcasts:", error);
      }
    };

    fetchBroadcasts();
    
    // Check for new broadcasts every 30 seconds
    const interval = setInterval(fetchBroadcasts, 30000);
    return () => clearInterval(interval);
  }, [token, getAuthHeaders]);

  const dismissBroadcast = async (broadcastId) => {
    try {
      await axios.post(
        `${API_URL}/live-support/broadcasts/${broadcastId}/dismiss`,
        {},
        { headers: getAuthHeaders() }
      );
      setBroadcasts((prev) => prev.filter((b) => b.id !== broadcastId));
    } catch (error) {
      console.error("Failed to dismiss broadcast:", error);
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case "warning":
        return {
          bg: "bg-yellow-50 dark:bg-yellow-900/30 border-yellow-400",
          icon: <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />,
          title: "text-yellow-800 dark:text-yellow-200",
        };
      case "urgent":
        return {
          bg: "bg-red-50 dark:bg-red-900/30 border-red-400",
          icon: <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />,
          title: "text-red-800 dark:text-red-200",
        };
      case "success":
        return {
          bg: "bg-green-50 dark:bg-green-900/30 border-green-400",
          icon: <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />,
          title: "text-green-800 dark:text-green-200",
        };
      case "info":
      default:
        return {
          bg: "bg-blue-50 dark:bg-blue-900/30 border-blue-400",
          icon: <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />,
          title: "text-blue-800 dark:text-blue-200",
        };
    }
  };

  if (broadcasts.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-[60] w-full max-w-md space-y-3">
      <AnimatePresence>
        {broadcasts.map((broadcast) => {
          const styles = getTypeStyles(broadcast.type);
          return (
            <motion.div
              key={broadcast.id}
              initial={{ opacity: 0, x: 100, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.95 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className={`${styles.bg} border-l-4 rounded-lg shadow-lg p-4`}
              data-testid={`broadcast-popup-${broadcast.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className={`font-semibold ${styles.title}`}>
                      {broadcast.title}
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 flex-shrink-0 hover:bg-black/10"
                      onClick={() => dismissBroadcast(broadcast.id)}
                      data-testid={`dismiss-broadcast-${broadcast.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm mt-1 text-gray-700 dark:text-gray-300">
                    {broadcast.message}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <Megaphone className="h-3 w-3" />
                    <span>From {broadcast.created_by_name}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
